/**
 * Distance & Bearing Calculation Utilities
 *
 * Pure math functions for geodesic measurements between two lat/lng points.
 * No external dependencies beyond standard JS Math.
 */

const EARTH_RADIUS_M = 6371000;
const METERS_PER_MILE = 1609.344;

/**
 * Convert degrees to radians.
 * @param {number} deg
 * @returns {number}
 */
function toRad(deg) {
  return (deg * Math.PI) / 180;
}

/**
 * Convert radians to degrees.
 * @param {number} rad
 * @returns {number}
 */
function toDeg(rad) {
  return (rad * 180) / Math.PI;
}

/**
 * Calculate the great-circle distance between two points using the haversine formula.
 *
 * @param {number} lat1 - Latitude of point 1 (decimal degrees)
 * @param {number} lng1 - Longitude of point 1 (decimal degrees)
 * @param {number} lat2 - Latitude of point 2 (decimal degrees)
 * @param {number} lng2 - Longitude of point 2 (decimal degrees)
 * @returns {number} Distance in meters
 */
export function haversineDistance(lat1, lng1, lat2, lng2) {
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_M * c;
}

/**
 * Calculate the initial bearing from point 1 to point 2.
 *
 * @param {number} lat1 - Latitude of point 1 (decimal degrees)
 * @param {number} lng1 - Longitude of point 1 (decimal degrees)
 * @param {number} lat2 - Latitude of point 2 (decimal degrees)
 * @param {number} lng2 - Longitude of point 2 (decimal degrees)
 * @returns {number} Bearing in degrees (0-360, where 0=N, 90=E, 180=S, 270=W)
 */
export function calculateBearing(lat1, lng1, lat2, lng2) {
  const phi1 = toRad(lat1);
  const phi2 = toRad(lat2);
  const dLambda = toRad(lng2 - lng1);

  const y = Math.sin(dLambda) * Math.cos(phi2);
  const x =
    Math.cos(phi1) * Math.sin(phi2) -
    Math.sin(phi1) * Math.cos(phi2) * Math.cos(dLambda);

  const theta = Math.atan2(y, x);
  return ((toDeg(theta) % 360) + 360) % 360;
}

/**
 * Map a bearing in degrees to one of 8 cardinal direction strings.
 *
 * @param {number} degrees - Bearing 0-360
 * @returns {string} Cardinal direction: N, NE, E, SE, S, SW, W, NW
 */
export function bearingToCardinal(degrees) {
  const normalized = ((degrees % 360) + 360) % 360;
  const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  const index = Math.round(normalized / 45) % 8;
  return directions[index];
}

/**
 * Format a distance value for human-readable display.
 *
 * @param {number} meters - Distance in meters
 * @param {object} [options]
 * @param {boolean} [options.imperial=false] - Include imperial (miles) output
 * @returns {string} Formatted string, e.g. "1.23 km" or "456 m" or "1.23 mi"
 */
export function formatDistance(meters, options = {}) {
  if (options.imperial) {
    const miles = meters / METERS_PER_MILE;
    return `${miles.toFixed(2)} mi`;
  }
  if (meters >= 1000) {
    return `${(meters / 1000).toFixed(2)} km`;
  }
  return `${Math.round(meters)} m`;
}

/**
 * Format a bearing for display with cardinal direction.
 *
 * @param {number} degrees - Bearing 0-360
 * @returns {string} Formatted string, e.g. "045° (NE)"
 */
export function formatBearing(degrees) {
  const normalized = ((degrees % 360) + 360) % 360;
  const padded = String(Math.round(normalized)).padStart(3, '0');
  const cardinal = bearingToCardinal(normalized);
  return `${padded}\u00B0 (${cardinal})`;
}
